import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import {
  HEALTH_INSURANCE_OTHER,
  MiscInfo,
  ParentInfo,
  resolveHealthInsuranceWith,
} from '@awo-care/shared-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'lib-student-misc-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatRadioModule,
  ],
  template: `
    <div class="form-container">
      <div class="section-header">
        <h3 i18n="@@student-misc-form.title">Weitere Angaben</h3>
        @if (canEdit() && readOnly() && !editMode()) {
        <button
          mat-button
          type="button"
          (click)="toggleEditMode()"
          class="edit-button"
        >
          <mat-icon>edit</mat-icon>
          <span i18n="@@common.edit">Bearbeiten</span>
        </button>
        } @if (editMode()) {
        <div class="edit-actions">
          <button
            mat-button
            type="button"
            (click)="cancelEdit()"
            class="cancel-button"
          >
            <mat-icon>close</mat-icon>
            <span i18n="@@common.cancel">Abbrechen</span>
          </button>
          <button
            mat-button
            type="button"
            (click)="saveChanges()"
            class="save-button"
            [disabled]="!form.valid || !form.dirty"
          >
            <mat-icon>save</mat-icon>
            <span i18n="@@common.save">Speichern</span>
          </button>
        </div>
        }
      </div>

      <form [formGroup]="form" class="form-content">
        <div class="section">
          <h3 i18n="@@student-misc-form.health-insurance-title">Angaben zur Krankenversicherung des Kindes</h3>
          <div class="form-fields">
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-misc-form.health-insurance-name">Name der Krankenkasse</mat-label>
              <input
                matInput
                formControlName="healthInsuranceName"
                [readonly]="readOnly() && !editMode()"
                i18n-placeholder="@@student-misc-form.health-insurance-name-placeholder"
                placeholder="Name der Krankenkasse"
                required
                data-testid="health-insurance-name"
              />
              @if (form.get('healthInsuranceName')?.hasError('required') &&
              form.get('healthInsuranceName')?.touched) {
              <mat-error i18n="@@student-misc-form.health-insurance-name-required">Name der Krankenkasse ist erforderlich</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-misc-form.health-insurance-with">Kind ist mitversichert bei</mat-label>
              <mat-select
                formControlName="healthInsuranceWith"
                required
                data-testid="health-insurance-with"
                (selectionChange)="onInsuranceWithChange()"
              >
                <mat-option [value]="null" i18n="@@common.please-select">Bitte auswählen</mat-option>
                <mat-option [value]="getParentName('parent1')">{{
                  getParentName('parent1')
                }}</mat-option>
                @if(parentData()?.parent2) {
                <mat-option [value]="getParentName('parent2')">{{
                  getParentName('parent2')
                }}</mat-option>
                }
                <mat-option
                  [value]="otherInsuranceValue"
                  i18n="@@student-misc-form.health-insurance-with-other-option"
                  >Sonstige (z.&nbsp;B. Großeltern)</mat-option
                >
              </mat-select>
              @if (form.get('healthInsuranceWith')?.hasError('required') &&
              form.get('healthInsuranceWith')?.touched) {
              <mat-error i18n="@@student-misc-form.health-insurance-with-required">Bitte wählen Sie eine Option aus</mat-error>
              }
            </mat-form-field>
            @if (form.get('healthInsuranceWith')?.value === otherInsuranceValue) {
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-misc-form.health-insurance-with-other-name">Name der mitversichernden Person</mat-label>
              <input
                matInput
                formControlName="healthInsuranceWithOther"
                [readonly]="readOnly() && !editMode()"
                i18n-placeholder="@@student-misc-form.health-insurance-with-other-placeholder"
                placeholder="Vor- und Nachname"
                required
                data-testid="health-insurance-with-other"
              />
              @if (form.get('healthInsuranceWithOther')?.hasError('required') &&
              form.get('healthInsuranceWithOther')?.touched) {
              <mat-error i18n="@@student-misc-form.health-insurance-with-other-required">Name ist erforderlich</mat-error>
              }
            </mat-form-field>
            }
          </div>
        </div>

        <div class="section">
          <h3 i18n="@@student-misc-form.emergency-contact-title">Wer ist im Notfall zuerst zu informieren/erreichbar?</h3>
          <div class="form-fields">
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-misc-form.emergency-name">Notfallkontakt</mat-label>
              <input
                matInput
                formControlName="emergencyName"
                [readonly]="readOnly() && !editMode()"
                i18n-placeholder="@@student-misc-form.emergency-name-placeholder"
                placeholder="Vor- und Nachname"
                required
                data-testid="emergency-name"
              />
              @if (form.get('emergencyName')?.hasError('required') &&
              form.get('emergencyName')?.touched) {
              <mat-error i18n="@@student-misc-form.emergency-name-required">Notfallkontakt ist erforderlich</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label i18n="@@student-misc-form.emergency-phone">Notfallkontakt Telefon</mat-label>
              <input
                matInput
                formControlName="emergencyPhone"
                [readonly]="readOnly() && !editMode()"
                i18n-placeholder="@@student-misc-form.emergency-phone-placeholder"
                placeholder="Rufnummer"
                required
                data-testid="emergency-phone"
              />
              @if (form.get('emergencyPhone')?.hasError('required') &&
              form.get('emergencyPhone')?.touched) {
              <mat-error i18n="@@student-misc-form.emergency-phone-required">Telefonnummer ist erforderlich</mat-error>
              }
            </mat-form-field>
          </div>
        </div>

        <div class="section">
          <h3 i18n="@@student-misc-form.measles-protection">Masernschutz</h3>
          <div class="form-group">
            <div class="radio-group">
              <div
                class="label"
                i18n="@@student-info-form.measles-vaccination-available"
              >
                Masernimpfung vorhanden
              </div>
              <mat-radio-group
                formControlName="measlesVaccination"
                data-testid="measles-vaccination"
              >
                <mat-radio-button [value]="true" i18n="@@common.yes"
                  >Ja</mat-radio-button
                >
                <mat-radio-button [value]="false" i18n="@@common.no"
                  >Nein</mat-radio-button
                >
              </mat-radio-group>
            </div>

            @if (form.get('measlesVaccination')?.value === false) {
            <div class="consent-section">
              <p
                class="info-text"
                i18n="@@student-info-form.health-office-certificate-required"
              >
                Eine Aufnahme ist nur nach Vorlage einer
                Unbedenklichkeitsbescheinigung des Gesundheitsamts möglich.
              </p>
              <mat-checkbox
                formControlName="measlesVaccinationConsent"
                i18n="@@student-info-form.notice-understood"
              >
                Ich habe den Hinweis gelesen und verstanden
              </mat-checkbox>
            </div>
            }
          </div>
        </div>

        <div class="section">
          <h3 i18n="@@student-misc-form.photo-consent-title">Bildfreigabe</h3>
          <div class="form-group">
            <mat-checkbox
              formControlName="photoConsentOptOut"
              data-testid="photo-consent-opt-out"
              i18n="@@student-misc-form.photo-consent-opt-out"
            >
              Hiermit wird der Nutzung von Fotos und Videos des Kindes für schulinterne Berichterstattung, Newsletter u.&#8239;ä. sowie für die Öffentlichkeitsarbeit des Trägers <strong>widersprochen</strong> (siehe AGB).
            </mat-checkbox>
          </div>
        </div>

        <!-- DEACTIVATED: After School Arrangement section
        <div class="section">
          <h3>Was passiert nach dem Ende der Betreuungszeit?</h3>
          <div class="form-fields">
            <mat-form-field appearance="outline" color="accent">
              <mat-label>Ende der Betreuungszeit (Bitte eine Option auswählen)</mat-label>
              <mat-select
                formControlName="afterSchoolArrangement"
                required
                data-testid="after-school-arrangement"
                (selectionChange)="onArrangementChange()"
              >
                <mat-option [value]="null">Bitte auswählen</mat-option>
                <mat-option value="picked_up">Abholung</mat-option>
                <mat-option value="independent_departure">Selbständiger Nachhauseweg</mat-option>
                <mat-option value="bus_transport">Bus Transport</mat-option>
              </mat-select>
              @if (form.get('afterSchoolArrangement')?.hasError('required') && form.get('afterSchoolArrangement')?.touched) {
              <mat-error>Bitte wählen Sie eine Option aus</mat-error>
              }
            </mat-form-field>
            @if(isPickedUp()) {
            <mat-form-field appearance="outline">
              <mat-label>Namen der Abholberechtigten</mat-label>
              <input
                matInput
                formControlName="afterSchoolArrangementDetails"
                [readonly]="readOnly() && !editMode()"
                placeholder="Bitte Namen angeben"
                required
              />
              @if (form.get('afterSchoolArrangementDetails')?.hasError('required') && form.get('afterSchoolArrangementDetails')?.touched) {
              <mat-error>Namen der Abholberechtigten sind erforderlich</mat-error>
              }
            </mat-form-field>
            <div class="info-note">
              <p>
                Bitte halten Sie uns über Änderungen der Abholberechtigten auf dem
                Laufenden.
              </p>
            </div>
            }
          </div>
        </div>
        -->
      </form>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .form-container {
        max-width: 800px;
        margin: 0 auto;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .section-header h3 {
        margin: 0;
        color: var(--theme-regularTextColor);
        font-size: 1.1rem;
        font-weight: 500;
      }

      .form-content {
        display: grid;
        gap: 2rem;
      }

      .section {
        display: grid;
        gap: 1rem;
      }

      .section h3 {
        margin: 0 0 1rem;
        color: rgba(0, 0, 0, 0.87);
        font-size: 1.2rem;
        font-weight: 500;
      }

      .form-fields {
        display: grid;
        gap: 1rem;
      }

      @media (min-width: 768px) {
        .form-fields {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      mat-form-field {
        width: 100%;
      }

      .info-note {
        color: #666;
        font-size: 0.875rem;
        line-height: 1.5;
        margin-top: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;

        p {
          margin: 0;
        }
      }

      .edit-button {
        color: var(--theme-accent);
      }

      .edit-actions {
        display: flex;
        gap: 1rem;
      }

      .cancel-button {
        color: var(--theme-warn);
      }

      .save-button {
        color: var(--theme-success);
      }

      .save-button:disabled {
        color: var(--theme-disabled);
      }

      .form-group {
        display: grid;
        gap: 1rem;
      }

      .radio-group {
        display: grid;
        gap: 0.5rem;
      }

      .label {
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.87);
      }

      mat-radio-group {
        display: flex;
        gap: 1rem;
      }

      .consent-section {
        display: grid;
        gap: 1rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;
      }

      .info-text {
        margin: 0;
        color: rgba(0, 0, 0, 0.7);
      }
    `,
  ],
})
export class StudentMiscFormComponent {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  data = input<MiscInfo | null>(null);
  parentData = input<Partial<ParentInfo | null>>({});
  studentHealthInfo = input<any>(null); // For accessing student's healthInfo
  readOnly = input<boolean>(false);
  /**
   * Blendet die Schaltflaeche „Bearbeiten" aus. Default `true` — nur Aufrufer,
   * die Schreibrechte pruefen, setzen das auf `false` (Teamleitung, #875).
   * Ersetzt keine Serverpruefung, die steht in `firestore.rules`.
   */
  canEdit = input<boolean>(true);
  formControl = output<AbstractControl>();
  formSubmit = output<MiscInfo>();

  // Track if we're waiting for an async save to complete
  private pendingSave = signal(false);

  editMode = input<boolean>(false);
  editModeChange = output<boolean>();

  isPickedUp = signal(false);

  /** Sentinel-Wert der "Sonstige"-Option im Template. */
  readonly otherInsuranceValue = HEALTH_INSURANCE_OTHER;

  form = this.fb.group({
    emergencyName: ['', [Validators.required]],
    emergencyPhone: ['', [Validators.required]],
    healthInsuranceName: ['', [Validators.required]],
    healthInsuranceWith: ['', [Validators.required]],
    healthInsuranceWithOther: [''],
    measlesVaccination: [true, [Validators.required]],
    measlesVaccinationConsent: [false],
    photoConsentOptOut: [false],
    // DEACTIVATED: After school arrangement fields
    // afterSchoolArrangement: [null as string | null, [Validators.required]],
    // afterSchoolArrangementDetails: [''],
  });

  private originalFormValue: any = null;

  constructor() {
    // Handle form disable/enable state
    effect(() => {
      const shouldDisable = this.readOnly() && !this.editMode();

      if (shouldDisable) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });

    // Setup conditional validators for measles vaccination
    this.setupMeaslesValidation();

    // Separate effects for different data sources
    effect(() => {
      const miscInfo = this.data();
      if (miscInfo) {
        // "Sonstige" wird am gesetzten Marker-Feld erkannt (kein Namens-Matching).
        const isOther = !!miscInfo.healthInsuranceWithOther;
        this.form.patchValue(
          {
            emergencyName: miscInfo.emergencyName || '',
            emergencyPhone: miscInfo.emergencyPhone || '',
            healthInsuranceName: miscInfo.healthInsuranceName || '',
            healthInsuranceWith: isOther
              ? HEALTH_INSURANCE_OTHER
              : miscInfo.healthInsuranceWith || '',
            healthInsuranceWithOther: miscInfo.healthInsuranceWithOther || '',
            photoConsentOptOut: miscInfo.photoConsentOptOut ?? false,
            // DEACTIVATED: After school arrangement fields
            // afterSchoolArrangement: miscInfo.afterSchoolArrangement || null,
            // afterSchoolArrangementDetails: miscInfo.afterSchoolArrangementDetails || '',
          },
          { emitEvent: false }
        );

        // Pflichtfeld-Validierung für das Freitextfeld passend zum geladenen Zustand
        this.updateOtherValidator(isOther);

        // DEACTIVATED: Initialize the signal based on the loaded data
        // this.isPickedUp.set(miscInfo.afterSchoolArrangement === 'picked_up');

        // Store original values for cancel functionality
        this.originalFormValue = this.form.getRawValue();
      }
    });

    // Separate effect for healthInfo data (measles vaccination)
    effect(() => {
      const healthInfo = this.studentHealthInfo();
      if (healthInfo) {
        this.form.patchValue(
          {
            measlesVaccination:
              healthInfo.measlesVaccination !== undefined
                ? healthInfo.measlesVaccination
                : true,
            // Reset consent to false in contract form - user must re-acknowledge
            measlesVaccinationConsent: false,
          },
          { emitEvent: false }
        );

        // If measles vaccination is false, immediately trigger validation
        if (healthInfo.measlesVaccination === false) {
          const consentControl = this.form.get('measlesVaccinationConsent');
          consentControl?.setValidators([Validators.requiredTrue]);
          consentControl?.markAsTouched(); // Ensure validation shows immediately
          consentControl?.updateValueAndValidity();
        }
      }
    });

    this.form.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.readOnly() && this.form.valid && this.form.dirty) {
          this.formControl.emit(this.form);
        }
      });
  }

  getParentName(parent: 'parent1' | 'parent2'): string {
    const parentInfo = this.parentData()?.[parent];
    if (!parentInfo) return '';
    return `${parentInfo.firstName} ${parentInfo.lastName}`;
  }

  /** Aktiviert/deaktiviert die Pflichtvalidierung des "Sonstige"-Freitextfelds. */
  onInsuranceWithChange(): void {
    const isOther =
      this.form.get('healthInsuranceWith')?.value === HEALTH_INSURANCE_OTHER;
    if (!isOther) {
      this.form.get('healthInsuranceWithOther')?.setValue('');
    }
    this.updateOtherValidator(isOther);
  }

  private updateOtherValidator(isOther: boolean): void {
    const control = this.form.get('healthInsuranceWithOther');
    if (isOther) {
      control?.setValidators([Validators.required]);
    } else {
      control?.clearValidators();
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  // DEACTIVATED: After school arrangement change handler
  // onArrangementChange(): void {
  //   const arrangement = this.form.get('afterSchoolArrangement')?.value;
  //   const isPickup = arrangement === 'picked_up';
  //   this.isPickedUp.set(isPickup);

  //   const detailsControl = this.form.get('afterSchoolArrangementDetails');

  //   if (isPickup) {
  //     detailsControl?.setValidators(Validators.required);
  //   } else {
  //     detailsControl?.clearValidators();
  //     detailsControl?.setValue('');
  //   }

  //   detailsControl?.updateValueAndValidity();
  // }

  // DEACTIVATED: Dynamic validation setup for after school arrangement
  // private setupDynamicValidation(): void {
  //   effect(() => {
  //     const detailsControl = this.form.get('afterSchoolArrangementDetails');
  //     if (this.isPickedUp()) {
  //       detailsControl?.setValidators(Validators.required);
  //     } else {
  //       detailsControl?.clearValidators();
  //     }
  //     detailsControl?.updateValueAndValidity({ emitEvent: false });
  //   });
  // }

  toggleEditMode(): void {
    this.editModeChange.emit(true);
  }

  cancelEdit(): void {
    if (this.originalFormValue) {
      this.form.patchValue(this.originalFormValue, { emitEvent: false });
      this.form.markAsPristine();
    }
    this.editModeChange.emit(false);
  }

  saveChanges(): void {
    if (this.form.valid && this.form.dirty) {
      const formValue = this.form.getRawValue();

      // Remove measles vaccination fields - they should stay in healthInfo only
      const { measlesVaccination, measlesVaccinationConsent, ...miscInfoData } =
        formValue;

      // "Sonstige": Freitext wird zum Klarnamen in healthInsuranceWith aufgelöst
      const resolvedInsurance = resolveHealthInsuranceWith(miscInfoData);

      // Mark that we're waiting for async save
      this.pendingSave.set(true);
      this.formSubmit.emit({
        ...miscInfoData,
        ...resolvedInsurance,
      } as MiscInfo);

      // Don't exit edit mode immediately - wait for parent to confirm save
      // this.editModeChange.emit(false); // Commented out for async saves
    }
  }

  // Method for parent to call when save is complete
  confirmSaveComplete(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      this.editModeChange.emit(false);
    }
  }

  // Method to reset form state without closing edit mode (for modal behavior)
  resetFormState(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      // Don't emit editModeChange - keep modal open
    }
  }

  // Method to mark form as saved without closing edit mode
  markAsSaved(): void {
    if (this.pendingSave()) {
      this.originalFormValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.pendingSave.set(false);
      // Stay in edit mode - don't emit editModeChange
    }
  }

  private setupMeaslesValidation(): void {
    // Measles vaccination consent validator
    this.form
      .get('measlesVaccination')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasMeaslesVaccination) => {
        const consentControl = this.form.get('measlesVaccinationConsent');
        if (hasMeaslesVaccination === false) {
          consentControl?.setValidators([Validators.requiredTrue]);
          consentControl?.markAsTouched(); // Ensure validation triggers
        } else {
          consentControl?.clearValidators();
          consentControl?.setValue(false); // Reset when not needed
        }
        consentControl?.updateValueAndValidity();
      });
  }
}
